import { apiClient } from '@/auth';

/**
 * MNT telemetry event API. Used by the patient mobile app to record
 * arbitrary feedback events; we use it to store clinician-submitted
 * meal feedback as `ClinicianMealFeedbackSubmitted` rows.
 *
 * Endpoint shape per MNT:
 *   PUT /api/telemetry/event/{name}/{key_b64}
 *   Body: { value: number, properties: object }
 *   Returns: 204 No Content
 *
 * Authentication is bearer-token — the server uses the token's user_id
 * as the row owner. For clinician meal feedback this is the clinician's
 * id, NOT the patient's; the patient-feedback pipelines won't pick up
 * the row. See the patient-app feedback writeup for the full attribution
 * caveat. To compensate we store `submitted_by_role: 'clinician'`,
 * `submitted_by_user_id`, and `on_behalf_of_patient_id` in `properties`
 * so a future backend change can flip the data into the patient stream
 * without re-collecting.
 */

/** UTF-8 → base64. Browser-safe (avoids `btoa` choking on non-ASCII). */
const toBase64 = (s: string): string => {
  if (typeof window === 'undefined') {
    return Buffer.from(s, 'utf-8').toString('base64');
  }
  // `unescape(encodeURIComponent(...))` is the classic trick to round-trip
  // UTF-8 through btoa without losing multi-byte chars. Modern alternative
  // (TextEncoder + Uint8Array.toBase64) isn't shipped widely yet.
  return window.btoa(unescape(encodeURIComponent(s)));
};

export type TelemetryEventRequest = {
  value: number;
  properties: Record<string, unknown>;
};

export type ClinicianMealFeedbackThumbs = 'thumbs_up' | 'thumbs_down';

export type ClinicianMealFeedbackTag = {
  id: string;
  label: string;
};

export type ClinicianMealFeedbackInput = {
  /** ID of the clinician submitting. Stored in properties for
   *  forward-compat; the row's `user_id` is also the clinician's. */
  clinicianId: number;
  /** Patient who owns the meal. Stored as `on_behalf_of_patient_id`
   *  so a future backend reader can route the row to the patient
   *  even though the telemetry `user_id` belongs to the clinician. */
  patientId: number;
  mealId: number;
  thumbs: ClinicianMealFeedbackThumbs | null;
  /** Optional free-text comment, ≤ 500 chars to keep the JSON payload
   *  well below the 16 KiB telemetry limit. */
  comments?: string;
  /** Optional categorized tags — canned issue labels chosen client-side. */
  tags?: ClinicianMealFeedbackTag[];
};

export type ClinicianUiFeedbackSentiment = 'positive' | 'neutral' | 'negative';

export type ClinicianUiFeedbackInput = {
  /** Clinician submitting; the row owner + stored in properties. */
  clinicianId: number;
  /** Overall sentiment; null if the clinician only left a comment. */
  sentiment: ClinicianUiFeedbackSentiment | null;
  /** Free-text comment, trimmed + capped server-side-safe (≤ 1000 chars). */
  comments?: string;
  /** Route the feedback was given on, e.g. `/patients/42/summary`. */
  page?: string;
};

export const telemetryApi = {
  /**
   * Submit a clinician's feedback on a patient's meal. Distinct event
   * name from the patient app's `PatientFeedbackFormSubmitted` so the
   * existing meal-gallery + Langfuse + MPQ readers don't pick it up
   * with the wrong attribution. When MNT adds a real "submit on behalf
   * of patient" endpoint, swap to that and (optionally) the canonical
   * event name in one place.
   */
  async submitClinicianMealFeedback(
    input: ClinicianMealFeedbackInput,
  ): Promise<void> {
    const name = 'ClinicianMealFeedbackSubmitted';
    const key = `meal:${input.mealId}`;
    const keyB64 = toBase64(key);

    const ordinal =
      input.thumbs === 'thumbs_up' ? 1 : input.thumbs === 'thumbs_down' ? 0 : null;

    const body: TelemetryEventRequest = {
      value: 1,
      properties: {
        // Mirrors PatientFeedbackFormSubmitted's shape so a future
        // backend reader can consume both event names with one parser.
        subject_id: input.mealId,
        response:
          input.thumbs == null
            ? null
            : { label: input.thumbs, ordinal },
        comments: input.comments?.trim() ?? '',
        tags: input.tags ?? [],
        // Clinician-specific markers for the inevitable backend bridge.
        submitted_by_role: 'clinician',
        submitted_by_user_id: input.clinicianId,
        on_behalf_of_patient_id: input.patientId,
      },
    };

    await apiClient.put(`/telemetry/event/${name}/${keyB64}`, body);
  },

  /**
   * Submit a clinician's feedback on the new UI. Same generic telemetry
   * event endpoint as the meal feedback, under a distinct event name. The
   * key is unique-per-submission (page + timestamp) so each comment is its
   * own row rather than overwriting the previous one.
   */
  async submitClinicianUiFeedback(input: ClinicianUiFeedbackInput): Promise<void> {
    const name = 'ClinicianUiFeedbackSubmitted';
    const key = `ui:${input.page ?? 'app'}:${Date.now()}`;
    const keyB64 = encodeURIComponent(toBase64(key));

    const ordinal =
      input.sentiment === 'positive'
        ? 1
        : input.sentiment === 'neutral'
          ? 0
          : input.sentiment === 'negative'
            ? -1
            : null;

    const body: TelemetryEventRequest = {
      value: 1,
      properties: {
        feedback_type: 'ui',
        sentiment: input.sentiment,
        sentiment_ordinal: ordinal,
        comments: input.comments?.trim().slice(0, 1000) ?? '',
        page: input.page ?? '',
        submitted_by_role: 'clinician',
        submitted_by_user_id: input.clinicianId,
        app: 'clinician-portal',
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      },
    };

    await apiClient.put(`/telemetry/event/${name}/${keyB64}`, body);
  },
};
